import { Component, OnDestroy, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'
import { Subject, takeUntil } from 'rxjs'

import { CentralImportResult, CentralRowView, CentralView, CentralWatchVerdict, CollectionStatus, CuadreRentas } from '../../data/interfaces/f098.interface'
import { CobranzaShellService } from '../../data/services/view/cobranza-shell.service'
import { entregarDescarga } from '../../data/utils/entregar-descarga.util'
import { ApiF098Service } from '../../data/services/api/api-f098.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Las tres tablas del libro de Contabilidad, en su orden. */
type TableKey = 'CURRENT_RENTS' | 'ARREARS' | 'OTHER' | 'CANCELLED'

interface TableDef {
  key: TableKey
  label: string
  hint: string
}

/**
 * Los catorce rubros y sus etiquetas, **en el orden de las columnas J:W de la hoja de Contabilidad**.
 *
 * Las llaves y los textos salen de `invoice-line-concept.enum.ts` del backend. No hay generación de
 * tipos entre los repos: si allá se agrega un rubro, aquí hay que traerlo a mano o su columna no se
 * pinta. `UNCLASSIFIED` va al final porque no existe en su hoja — es columna nuestra, y existe para
 * que una partida sin clasificar se VEA en vez de desaparecer dentro de un total.
 */
const CONCEPTOS: { key: string; label: string }[] = [
  { key: 'RENT', label: 'Renta' },
  { key: 'RATIFICATION_EXPENSES', label: 'Gastos de ratificación' },
  { key: 'DOWN_PAYMENT', label: 'Enganche' },
  { key: 'OPENING_COMMISSION', label: 'Comisión por apertura' },
  { key: 'ADVANCED_RENTS', label: 'Rentas anticipadas' },
  { key: 'INSURANCE', label: 'Seguro' },
  { key: 'GPS', label: 'GPS' },
  { key: 'FLEET_MANAGEMENT', label: 'Adm. flotilla' },
  { key: 'LATE_INTEREST', label: 'Intereses moratorios' },
  { key: 'INTEREST', label: 'Intereses' },
  { key: 'VEHICLE_MANAGEMENT', label: 'Gestión vehicular' },
  { key: 'RESIDUAL', label: 'Residuales' },
  { key: 'ADVANCE', label: 'Anticipo' },
  { key: 'ARMORING', label: 'Blindaje' },
  { key: 'UNCLASSIFIED', label: 'Sin clasificar' },
]

/**
 * CENTRAL: el libro de facturas del mes, **derivado**.
 *
 * No es una recaptura de la hoja de Contabilidad: cada renglón sale de cruzar la factura con las
 * aplicaciones de pago que ya viven en la plataforma. La hoja sigue siendo el insumo; esto es lo que
 * la plataforma sabe decir por su cuenta.
 *
 * **El aviso de «hoja no subida» va arriba del todo y no es decorativo.** Una CENTRAL vacía se ve
 * idéntica a un mes sin facturas —tablas en blanco, totales en cero—, así que sin ese renglón el
 * operador leería «no se facturó nada» donde dice «nadie subió la hoja».
 */
@Component({
  selector: 'app-central',
  templateUrl: './central.component.html',
  styleUrls: ['./central.component.scss'],
})
export class CentralComponent implements OnInit, OnDestroy {
  readonly tables: TableDef[] = [
    { key: 'CURRENT_RENTS', label: 'Rentas del mes', hint: 'Las rentas que vencen dentro del mes' },
    { key: 'ARREARS', label: 'Rezago', hint: 'Rentas de meses anteriores que seguían abiertas al empezar el mes: las cobradas en el mes y las que siguen sin cobrarse' },
    { key: 'OTHER', label: 'Otras facturas', hint: 'Gestoría, seguros, placas y todo lo que no es renta' },
    { key: 'CANCELLED', label: 'Canceladas', hint: 'No suman a ningún total: se listan para poder explicarlas' },
  ]

  readonly conceptos = CONCEPTOS

  /**
   * Las clasificaciones que Contabilidad puede poner. Salen de `invoice-classification.enum.ts`.
   *
   * `UNCLASSIFIED` **no está** a propósito: es el hueco del que se sale, no un destino. Para «ya lo
   * miré y no encaja en ninguna» existe `OTHER`, que es una decisión y no una ausencia.
   */
  readonly clasificaciones = [
    { key: 'RENT', label: 'Renta' },
    { key: 'INSURANCE', label: 'Seguro' },
    { key: 'LATE_INTEREST', label: 'Intereses moratorios' },
    { key: 'DOWN_PAYMENT', label: 'Pago inicial' },
    { key: 'RESIDUAL', label: 'Residuales' },
    { key: 'VEHICLE_MANAGEMENT', label: 'Gestión vehicular' },
    { key: 'ADMINISTRATION', label: 'Administración' },
    { key: 'ADMINISTRATIVE_EXPENSES', label: 'Gastos administrativos' },
    { key: 'INTEREST', label: 'Intereses' },
    { key: 'OTHER', label: 'Otros' },
  ]

  // ── La captura de Contabilidad ──────────────────────────────────────────────────────────────
  //* La bandera va aparte del dato: `[(visible)]` de `p-dialog` quiere un booleano.
  mostrarCorreccion = false
  editando: CentralRowView | null = null
  edClasificacion = ''
  edMes = ''
  edMotivo = ''
  edNotas = ''
  /** El error de la corrección se dice DENTRO del diálogo: un toast detrás de la máscara no se ve. */
  edError: string | null = null
  guardando = false

  mostrarDeposito = false
  depositando: CentralRowView | null = null
  depMonto: number | null = null
  depBanco = ''
  depReferencia = ''
  depFecha = ''

  central: CentralView | null = null
  watch: CentralWatchVerdict | null = null

  // ── Importar la hoja CENTRAL ────────────────────────────────────────────────────────────────
  /*
   *! La API para importar la hoja existía desde el paso 4 y **ninguna pantalla la llamaba**. El
   *! aviso le pedía a Contabilidad «súbela» sin darle dónde: por eso nadie entendía a qué se refería.
   *
   * Dos pasos, como toda carga que escribe: leer primero (no escribe nada) y confirmar después. Lo
   * que no entra se enseña con su motivo antes de importar, no después.
   */
  mostrarImportar = false
  archivoHoja: File | null = null
  nombreHoja = ''
  vistaPrevia: CentralImportResult | null = null
  leyendo = false
  importando = false

  /*
   * El cuadre de rentas: lo que Contabilidad capturó en su hoja contra lo que dice el CFDI.
   *
   *! El control existía en la API desde el paso 4 y **no lo pintaba ninguna pantalla**. Un control
   *! que nadie ve no controla nada: la pregunta que contesta —«¿los montos cuadran?»— es la que
   *! Contabilidad hace cada mes, y sin él la respuesta seguía saliendo de revisar la hoja a mano.
   *
   * Va aquí y no en una pestaña propia porque valida ESTAS facturas: separarlo obligaría a cambiar
   * de pantalla para saber si lo que se está mirando es de fiar.
   */
  cuadre: CuadreRentas | null = null
  cuadreAbierto = false
  exportandoCuadre = false
  loading = false
  tabla: TableKey = 'CURRENT_RENTS'
  /** El renglón abierto, para ver sus depósitos sin salir de la tabla. */
  abierto: string | null = null
  exportando = false

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiF098Service,
    private readonly shell: CobranzaShellService,
    private readonly auth: AuthService,
    private readonly toast: MessageService,
  ) {}

  /** Importar crea y actualiza facturas: la misma compuerta que la carga masiva de Comprobantes. */
  get puedeImportar(): boolean {
    return this.auth.hasModulePermission('invoices', 'create')
  }

  abrirImportar(): void {
    this.mostrarImportar = true
    this.archivoHoja = null
    this.nombreHoja = ''
    this.vistaPrevia = null
  }

  elegirHoja(ev: Event): void {
    this.archivoHoja = (ev.target as HTMLInputElement).files?.[0] ?? null
    //* Otro archivo invalida lo que se previsualizó: importar sin volver a leer escribiría algo que no se vio.
    this.vistaPrevia = null
  }

  leerHoja(): void {
    if (!this.archivoHoja) return
    this.leyendo = true
    this.api.centralPreview(this.archivoHoja, this.nombreHoja.trim() || undefined).subscribe((r) => {
      this.leyendo = false
      if (r.error) return this.toast.add({ severity: 'error', summary: 'No se pudo leer la hoja', detail: r.msg, life: 6000 })
      this.vistaPrevia = r.data
    })
  }

  importarHoja(): void {
    if (!this.archivoHoja || !this.vistaPrevia) return
    this.importando = true
    this.api.centralImport(this.archivoHoja, this.nombreHoja.trim() || undefined).subscribe((r) => {
      this.importando = false
      if (r.error) return this.toast.add({ severity: 'error', summary: 'No se importó', detail: r.msg, life: 6000 })
      const t = r.data?.totals
      this.mostrarImportar = false
      this.toast.add({ severity: 'success', summary: 'Hoja importada', detail: `${t?.created ?? 0} nuevas · ${t?.updated ?? 0} actualizadas · ${t?.skipped ?? 0} sin importar`, life: 6000 })
      //* El embudo y Comprobantes también leen estas facturas: se refresca todo el cascarón, no solo esta vista.
      this.shell.refresh()
    })
  }

  /** Lo que no va a entrar, con su motivo: es lo que hay que corregir en la hoja antes de importar. */
  get omitidas() {
    return (this.vistaPrevia?.rows || []).filter((f) => f.status === 'SKIPPED')
  }

  /** Corregir mueve a qué mes pertenece un ingreso: exige `invoices:update`, no basta con verlo. */
  get puedeCapturar(): boolean {
    return this.auth.hasModulePermission('invoices', 'update')
  }

  abrirCorreccion(f: CentralRowView, ev: Event): void {
    ev.stopPropagation()
    this.editando = f
    this.mostrarCorreccion = true
    this.edClasificacion = f.classification || ''
    this.edMes = f.month || ''
    this.edMotivo = ''
    this.edNotas = f.notes || ''
    this.edError = null
  }

  /** Lo que impide guardar la corrección, con las mismas reglas del servidor. */
  get problemaCorreccion(): string | null {
    if (this.edMes && !/^\d{4}-(0[1-9]|1[0-2])$/.test(this.edMes.trim())) return 'El mes contable se escribe AAAA-MM (por ejemplo 2026-08).'
    if (this.edNotas.trim().length > 1000) return `Las notas admiten 1000 caracteres; llevas ${this.edNotas.trim().length}.`
    return null
  }

  guardarCorreccion(): void {
    if (!this.editando) return
    if (this.problemaCorreccion) {
      this.edError = this.problemaCorreccion
      return
    }
    this.guardando = true
    this.edError = null
    const dto: any = { reason: this.edMotivo.trim() || undefined }
    if (this.edClasificacion) dto.classification = this.edClasificacion
    if (this.edMes) dto.month = this.edMes.trim()
    //* Solo se mandan si cambiaron: mandar las mismas notas no debe dejar una entrada vacía en la bitácora.
    if (this.edNotas.trim() !== (this.editando.notes || '').trim()) dto.notes = this.edNotas.trim() || null

    this.api.patchAccounting(this.editando.invoiceId, dto).subscribe((r) => {
      this.guardando = false
      if (r.error) {
        this.edError = r.msg
        return
      }
      this.mostrarCorreccion = false
      this.toast.add({ severity: 'success', summary: 'Factura corregida', detail: 'El cambio quedó en la bitácora con tu nombre' })
      this.cargar()
    })
  }

  abrirDeposito(f: CentralRowView, ev: Event): void {
    ev.stopPropagation()
    this.depositando = f
    this.mostrarDeposito = true
    //* Se propone lo que falta por cobrar: es el importe que casi siempre se captura.
    this.depMonto = f.pending > 0 ? f.pending : null
    this.depBanco = ''
    this.depReferencia = ''
    this.depFecha = ''
  }

  guardarDeposito(): void {
    if (!this.depositando || !this.depMonto || this.depMonto <= 0) return
    this.guardando = true
    this.api
      .addDeposit(this.depositando.invoiceId, {
        amount: this.depMonto,
        bank: this.depBanco.trim() || null,
        reference: this.depReferencia.trim() || null,
        paidAt: this.depFecha || null,
      })
      .subscribe((r) => {
        this.guardando = false
        if (r.error) return this.toast.add({ severity: 'error', summary: 'No se registró', detail: r.msg, life: 6000 })
        this.mostrarDeposito = false
        this.toast.add({ severity: 'success', summary: 'Depósito registrado', detail: 'Ya cuenta como cobrado en CENTRAL' })
        this.cargar()
      })
  }

  quitarDeposito(f: CentralRowView, depositId: string, ev: Event): void {
    ev.stopPropagation()
    this.api.removeDeposit(f.invoiceId, depositId).subscribe((r) => {
      if (r.error) return this.toast.add({ severity: 'error', summary: 'No se quitó', detail: r.msg })
      this.toast.add({ severity: 'success', summary: 'Depósito quitado', detail: 'La bitácora conserva lo que decía' })
      this.cargar()
    })
  }

  /** Las filas del cuadre que hay que mirar: las que difieren van primero, y luego lo no comparable. */
  get cuadreFilas(): any[] {
    const orden: Record<string, number> = { DIFERENCIA: 0, SIN_CFDI: 1, SIN_CAPTURA: 2, CUADRADO: 3 }
    return [...(this.cuadre?.rows || [])].sort((a, b) => (orden[a.verdict] ?? 9) - (orden[b.verdict] ?? 9))
  }

  /*
   * El porcentaje que cuadra.
   *
   *! `null` es «no se pudo medir», NO «0 %»: un mes sin una sola factura con los dos lados no cuadra
   *! mal, es que no hay nada que comparar. Enseñarlo como 0 % diría que todo está descuadrado.
   */
  get cuadrePct(): string {
    const r = this.cuadre?.totals?.matchRate
    return r == null ? 'sin medir' : `${Math.round(r * 1000) / 10}%`
  }

  veredicto(v: string): { label: string; sev: string } {
    return (
      {
        CUADRADO: { label: 'cuadra', sev: 'success' },
        DIFERENCIA: { label: 'difiere', sev: 'danger' },
        SIN_CFDI: { label: 'sin CFDI', sev: 'warning' },
        SIN_CAPTURA: { label: 'sin captura en la hoja', sev: 'info' },
      }[v] ?? { label: v, sev: 'info' }
    )
  }

  /** Cómo se lee una clasificación en la tabla. */
  etiquetaClasificacion(key: string | null): string {
    if (!key || key === 'UNCLASSIFIED') return 'sin clasificar'
    return this.clasificaciones.find((c) => c.key === key)?.label || key
  }

  ngOnInit(): void {
    this.shell.month$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** `YYYY-MM` del mes del cascarón. Es lo que entiende la API. */
  private get month(): string {
    const d = this.shell.month || new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  cargar(): void {
    this.loading = true
    const mes = this.month
    this.api.central(mes).subscribe((r) => {
      this.loading = false
      this.central = r.data
      if (r.error) this.toast.add({ severity: 'error', summary: 'CENTRAL', detail: r.msg })
    })
    //* El aviso y el cuadre van aparte: que fallen no puede dejar la pantalla sin sus tablas.
    this.api.centralWatch(mes).subscribe((r) => (this.watch = r.data))
    this.api.cuadre(mes).subscribe((r) => (this.cuadre = r.data))
  }

  /** Los renglones de la tabla abierta. Las canceladas viven fuera de `tables`. */
  get filas(): CentralRowView[] {
    if (!this.central) return []
    return this.tabla === 'CANCELLED' ? this.central.cancelled : this.central.tables[this.tabla] || []
  }

  cuantas(key: TableKey): number {
    if (!this.central) return 0
    return key === 'CANCELLED' ? this.central.cancelled.length : (this.central.tables[key] || []).length
  }

  /** Solo se pintan las columnas de rubro que ALGUNA fila usa: el resto serían 10 columnas en blanco. */
  get conceptosUsados(): { key: string; label: string }[] {
    const filas = this.filas
    return this.conceptos.filter((c) => filas.some((f) => this.monto(f, c.key) !== 0))
  }

  /** La etiqueta de la tabla abierta, para que el vacío diga de QUÉ está vacío. */
  get etiquetaTabla(): string {
    return this.tables.find((t) => t.key === this.tabla)?.label || ''
  }

  /** El importe de un rubro en una fila. Método y no acceso por corchetes en la plantilla: el
   *  desglose es un diccionario abierto y un rubro nuevo no puede romper la compilación. */
  monto(row: CentralRowView, concepto: string): number {
    return row?.concepts?.[concepto] || 0
  }

  abrir(row: CentralRowView): void {
    this.abierto = this.abierto === row.invoiceId ? null : row.invoiceId
  }

  severidad(estado: CollectionStatus): string {
    if (estado === 'PAGADO') return 'success'
    return estado === 'PAGADO_PARCIAL' ? 'warning' : 'info'
  }

  etiqueta(estado: CollectionStatus): string {
    return { PAGADO: 'Pagada', PAGADO_PARCIAL: 'Parcial', PENDIENTE: 'Pendiente' }[estado] || estado
  }

  /*
   * Baja la hoja CENTRAL con la estructura de la de Contabilidad: sus tres tablas, sus franjas y sus
   * 38 columnas. Sale del mismo generador que el libro del mes, así que no hay dos versiones.
   */
  exportar(): void {
    this.exportando = true
    this.api.hoja('central', this.month).subscribe((d) => {
      this.exportando = false
      entregarDescarga(d, `CENTRAL ${this.month}.xlsx`, this.toast, 'CENTRAL')
    })
  }

  /** El cuadre se descarga aparte de CENTRAL: es otra pregunta y otro archivo. */
  exportarCuadre(ev: Event): void {
    ev.stopPropagation()
    this.exportandoCuadre = true
    this.api.hoja('cuadre', this.month).subscribe((d) => {
      this.exportandoCuadre = false
      entregarDescarga(d, `CUADRE RENTAS ${this.month}.xlsx`, this.toast, 'Cuadre de rentas')
    })
  }
}
